import { useMutation } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { DELETE_STANDARD_POLICY, GET_STANDARD_POLICIES } from '@system-config/policy-management/graphql';
import { useState, useCallback } from 'react';

interface DeleteStandardPolicyResponse {
  deleteStandardPolicy: boolean;
}

export function useDeleteStandardPolicy() {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [deleteStandardPolicyMutation, { loading }] = useMutation<DeleteStandardPolicyResponse>(DELETE_STANDARD_POLICY);

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const deleteStandardPolicy = async (policyId: string): Promise<boolean> => {
    try {
      track('DeleteStandardPolicyInitiated', {
        policyId,
      });

      await deleteStandardPolicyMutation({
        variables: { input: { policyId } },
        refetchQueries: [{ query: GET_STANDARD_POLICIES }],
        awaitRefetchQueries: true,
      });

      track('DeleteStandardPolicySucceeded', {
        policyId,
      });

      return true;
    } catch (error) {
      track('DeleteStandardPolicyFailed', {
        policyId,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    deleteStandardPolicy,
    loading,
    errors,
    resetErrors,
  };
}

export default useDeleteStandardPolicy;
